/**
 * ObjectOutlineEffect - unified object outline for WebGL and WebGPU using ID-buffer rendering.
 */

import { BlendFunction, Effect } from 'postprocessing';
import * as THREE from 'three';
import {
  float,
  Fn,
  max,
  min,
  mix,
  perspectiveDepthToViewZ,
  screenUV,
  select,
  smoothstep,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { MeshBasicNodeMaterial, type Node, NodeMaterial, QuadMesh } from 'three/webgpu';

import { SceneComponent } from '../../../components/SceneComponent.js';
import { GenesysScene } from '../../GenesysScene.js';
import {
  type EffectContext,
  IPostProcessEffect,
  type WebGLEffectData,
  type WebGPUComposeContext,
  type WebGPUEffectNode,
} from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, type ObjectOutlineEffectConfig, PostProcessPass, PostProcessRenderOrder } from '../PostProcessConfig.js';
import { SceneDepthRenderer } from '../utils/SceneDepthRenderer.js';

import type { Actor } from '../../../actors/Actor.js';
import type { WebGPURenderer } from '../../Renderer.js';

/** Constructor/init shape for object outline low-level implementations. */
type ObjectOutlineEffectInit = Partial<Pick<ObjectOutlineEffectConfig,
  'edgeStrength'
  | 'edgeThickness'
  | 'visibleEdgeColor'
  | 'hiddenEdgeColor'
  | 'showHiddenEdge'
  | 'resolutionScale'
  | 'useActorGrouping'
  | 'edgeBlur'
  | 'edgeGlow'
>> & {
  blendFunction?: BlendFunction;
  sharedSceneDepthTexture?: THREE.Texture | null;
};

/** Default values for ObjectOutlineEffect options */
export const OBJECT_OUTLINE_DEFAULTS = {
  edgeStrength: 1.0,
  edgeThickness: 1.5,
  showHiddenEdge: true,
  visibleEdgeColor: 0xffffff,
  hiddenEdgeColor: 0x888888,
  resolutionScale: 2.0,
  useActorGrouping: true,
  edgeBlur: 0,
  edgeGlow: 0,
} as const satisfies Required<Omit<ObjectOutlineEffectInit, 'blendFunction' | 'sharedSceneDepthTexture'>>;

/** An outline effect based on object ID buffer rendering for per-object outlines */
class WebGLObjectOutlineEffect extends Effect {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private _resolutionScale: number;
  private _useActorGrouping: boolean;
  private _edgeBlur: number;

  // Render targets
  private renderTargetID: THREE.WebGLRenderTarget;
  private renderTargetEdge: THREE.WebGLRenderTarget;
  private renderTargetBlur: THREE.WebGLRenderTarget;

  // Materials
  private idMaterial: ObjectIDMaterial;
  private idMaterialInstanced: ObjectIDMaterial;
  private edgeDetectionMaterial: EdgeDetectionMaterial;
  private blurMaterial: BlurMaterial;

  // Fullscreen quad for edge detection pass
  private fullscreenQuad: THREE.Mesh;
  private orthoCamera: THREE.OrthographicCamera;
  private quadScene: THREE.Scene;

  // Selection management - tracks Actor -> Object3D[] mapping
  public readonly selection: ActorSelection = new ActorSelection();

  // Hidden objects that should not render outlines (synced from PostProcessManager)
  private hiddenObjects: ReadonlySet<THREE.Object3D> = new Set();

  // Excluded objects that should not have post-processing (including outlines)
  private excludedObjects: ReadonlySet<THREE.Object3D> = new Set();

  // Layer used to identify excluded objects (default: 30)
  private excludedLayer: number = 30;
  private excludedLayerMask: THREE.Layers = new THREE.Layers();

  // Temporary storage for material swapping during ID render
  private objectMaterialMap = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();

  constructor(scene: THREE.Scene, camera: THREE.Camera, options: ObjectOutlineEffectInit = {}) {
    super('ObjectOutlineEffect', fragmentShader, {
      blendFunction: options.blendFunction ?? BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['edgeTexture', new THREE.Uniform(null)],
        ['edgeStrength', new THREE.Uniform(options.edgeStrength ?? OBJECT_OUTLINE_DEFAULTS.edgeStrength)],
        ['visibleEdgeColor', new THREE.Uniform(new THREE.Color(options.visibleEdgeColor ?? OBJECT_OUTLINE_DEFAULTS.visibleEdgeColor))]
      ])
    });

    this.scene = scene;
    this.camera = camera;
    this._resolutionScale = options.resolutionScale ?? OBJECT_OUTLINE_DEFAULTS.resolutionScale;
    this._useActorGrouping = options.useActorGrouping ?? OBJECT_OUTLINE_DEFAULTS.useActorGrouping;
    this._edgeBlur = options.edgeBlur ?? OBJECT_OUTLINE_DEFAULTS.edgeBlur;

    // Initialize exclusion layer mask
    this.excludedLayerMask.set(this.excludedLayer);

    const _screenW = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const _screenH = typeof window !== 'undefined' ? window.innerHeight : 1080;
    const width = Math.max(1, Math.floor(_screenW * this._resolutionScale));
    const height = Math.max(1, Math.floor(_screenH * this._resolutionScale));

    // Object ID buffer - each object rendered with unique color
    this.renderTargetID = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: true,
      stencilBuffer: false
    });
    this.renderTargetID.texture.name = 'ObjectOutline.ID';

    // Edge detection result
    this.renderTargetEdge = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false
    });
    this.renderTargetEdge.texture.name = 'ObjectOutline.Edge';

    // Blur result
    this.renderTargetBlur = new THREE.WebGLRenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: false,
      stencilBuffer: false
    });
    this.renderTargetBlur.texture.name = 'ObjectOutline.Blur';

    // Link edge texture to effect uniform (will be swapped to blur if blur is enabled)
    this.uniforms.get('edgeTexture')!.value = this.renderTargetEdge.texture;

    // Materials - separate instances for regular and instanced meshes to ensure correct shader compilation
    this.idMaterial = new ObjectIDMaterial();
    this.idMaterialInstanced = new ObjectIDMaterial();
    this.edgeDetectionMaterial = new EdgeDetectionMaterial(this.renderTargetID.texture, options.edgeThickness ?? OBJECT_OUTLINE_DEFAULTS.edgeThickness);
    this.edgeDetectionMaterial.setSize(width, height);
    this.blurMaterial = new BlurMaterial(this.renderTargetEdge.texture);
    this.blurMaterial.setSize(width, height);

    // Fullscreen quad for edge detection
    const quadGeometry = new THREE.PlaneGeometry(2, 2);
    this.fullscreenQuad = new THREE.Mesh(quadGeometry, this.edgeDetectionMaterial);
    this.fullscreenQuad.frustumCulled = false;
    this.orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quadScene = new GenesysScene();
    this.quadScene.add(this.fullscreenQuad);
  }

  /** Sets the main scene */
  setScene(scene: THREE.Scene): void {
    this.scene = scene;
  }

  /** Sets the camera */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /** Gets the edge strength */
  get edgeStrength(): number {
    return this.uniforms.get('edgeStrength')!.value;
  }

  /** Sets the edge strength */
  set edgeStrength(value: number) {
    this.uniforms.get('edgeStrength')!.value = value;
  }

  /** Gets the edge width in pixels */
  get edgeWidth(): number {
    return this.edgeDetectionMaterial.edgeWidth;
  }

  /** Sets the edge width in pixels */
  set edgeWidth(value: number) {
    this.edgeDetectionMaterial.edgeWidth = value;
  }

  /** Gets the visible edge color */
  get visibleEdgeColor(): number {
    return this.uniforms.get('visibleEdgeColor')!.value.getHex();
  }

  /** Sets the visible edge color */
  set visibleEdgeColor(value: number) {
    this.uniforms.get('visibleEdgeColor')!.value.setHex(value);
  }

  /** Gets the resolution scale */
  get resolutionScale(): number {
    return this._resolutionScale;
  }

  /** Sets the resolution scale and updates render targets */
  set resolutionScale(value: number) {
    const oldScale = this._resolutionScale;
    this._resolutionScale = value;
    // Update render targets with new resolution scale
    // Get current size from render targets
    const currentWidth = this.renderTargetID.width;
    const currentHeight = this.renderTargetID.height;
    // Calculate original viewport size (before scaling)
    const originalWidth = oldScale > 0 ? currentWidth / oldScale : currentWidth;
    const originalHeight = oldScale > 0 ? currentHeight / oldScale : currentHeight;
    // Apply new scale
    const newWidth = Math.max(1, Math.floor(originalWidth * value));
    const newHeight = Math.max(1, Math.floor(originalHeight * value));
    this.renderTargetID.setSize(newWidth, newHeight);
    this.renderTargetEdge.setSize(newWidth, newHeight);
    this.renderTargetBlur.setSize(newWidth, newHeight);
    this.edgeDetectionMaterial.setSize(newWidth, newHeight);
    this.blurMaterial.setSize(newWidth, newHeight);
  }

  /** Gets whether actor grouping is enabled */
  get useActorGrouping(): boolean {
    return this._useActorGrouping;
  }

  /** Sets whether actor grouping is enabled (all meshes in an actor share same ID) */
  set useActorGrouping(value: boolean) {
    this._useActorGrouping = value;
  }

  /** Gets the edge blur amount */
  get edgeBlur(): number {
    return this._edgeBlur;
  }

  /** Sets the edge blur amount (0 = no blur) */
  set edgeBlur(value: number) {
    this._edgeBlur = Math.max(0, value);
    this.blurMaterial.blurAmount = this._edgeBlur;
    // Update which texture the effect uses
    this.uniforms.get('edgeTexture')!.value = this._edgeBlur > 0 ? this.renderTargetBlur.texture : this.renderTargetEdge.texture;
  }

  /** Encode object ID to RGB color (supports up to 16.7M unique objects) */
  private encodeObjectID(id: number): THREE.Vector3 {
    const r = (id & 0xFF) / 255;
    const g = ((id >> 8) & 0xFF) / 255;
    const b = ((id >> 16) & 0xFF) / 255;
    return new THREE.Vector3(r, g, b);
  }

  @THREE.profile
  override update(renderer: THREE.WebGLRenderer, _inputBuffer: THREE.WebGLRenderTarget, _deltaTime?: number): void {
    // Prune invalid actors from selection before rendering
    this.selection.pruneInvalidActors();

    if (this.selection.size === 0) {
      // Clear edge textures when nothing is selected
      const currentRenderTarget = renderer.getRenderTarget();
      renderer.setRenderTarget(this.renderTargetEdge);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      // Also clear blur texture if blur is enabled to prevent stale outlines
      if (this._edgeBlur > 0) {
        renderer.setRenderTarget(this.renderTargetBlur);
        renderer.clear();
      }
      renderer.setRenderTarget(currentRenderTarget);
      return;
    }

    // Save renderer state
    const currentRenderTarget = renderer.getRenderTarget();
    const currentAutoClear = renderer.autoClear;
    const currentClearColor = renderer.getClearColor(new THREE.Color());
    const currentClearAlpha = renderer.getClearAlpha();

    try {
      renderer.autoClear = false;

      // Step 1: Render selected objects to ID buffer
      // Excluded objects are NOT rendered - they rely on depth from main scene render
      renderer.setRenderTarget(this.renderTargetID);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();

      this.objectMaterialMap.clear();
      let idCounter = 1;

      // Iterate through all actors and their meshes
      for (const actor of this.selection) {
        // Re-collect meshes each frame to handle dynamically loaded content (e.g., GLTFs)
        const meshes = this.selection.refreshMeshesForActor(actor);

        // When using actor grouping, all meshes in an actor share the same ID (no internal edges)
        const actorID = this._useActorGrouping ? this.encodeObjectID(idCounter++) : null;

        for (const mesh of meshes) {
          // Check if mesh or any ancestor is hidden, excluded, or in the hiddenObjects set
          let isVisible = mesh.visible;
          let isHidden = false;
          let isExcluded = false;
          let obj: THREE.Object3D | null = mesh;
          while (obj) {
            if (!obj.visible) { isVisible = false; }
            if (this.hiddenObjects.has(obj)) { isHidden = true; }
            // Check if object is on the exclusion layer (more efficient than set lookup)
            if (obj.layers.test(this.excludedLayerMask)) { isExcluded = true; }
            obj = obj.parent;
          }
          // Skip hidden/excluded meshes - don't render outlines for invisible, hidden, or excluded objects
          if (!isVisible || !mesh.material || isHidden || isExcluded) {
            continue;
          }

          // Skip if already processed (same mesh in multiple actors) to avoid storing ID material as original
          if (this.objectMaterialMap.has(mesh)) {
            continue;
          }

          // Store original material
          this.objectMaterialMap.set(mesh, mesh.material);

          // Set ID material - use instanced version for InstancedMesh to ensure correct shader compilation
          const objectID = actorID ?? this.encodeObjectID(idCounter++);
          const material = isInstancedMesh(mesh) ? this.idMaterialInstanced : this.idMaterial;
          material.setObjectID(objectID);
          mesh.material = material;

          // Render this mesh alone
          renderer.render(mesh, this.camera);
        }
      }

      // Restore original materials
      for (const [mesh, material] of this.objectMaterialMap) {
        mesh.material = material;
      }

      // Step 2: Edge detection pass (only if meshes were rendered)
      renderer.setRenderTarget(this.renderTargetEdge);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      if (this.objectMaterialMap.size > 0) {
        this.fullscreenQuad.material = this.edgeDetectionMaterial;
        renderer.render(this.quadScene, this.orthoCamera);
      }

      // Step 3: Optional blur pass (only if meshes were rendered)
      if (this._edgeBlur > 0) {
        renderer.setRenderTarget(this.renderTargetBlur);
        renderer.setClearColor(0x000000, 0);
        renderer.clear();
        if (this.objectMaterialMap.size > 0) {
          this.fullscreenQuad.material = this.blurMaterial;
          renderer.render(this.quadScene, this.orthoCamera);
        }
      }

    } finally {
      // Restore renderer state
      renderer.setRenderTarget(currentRenderTarget);
      renderer.setClearColor(currentClearColor, currentClearAlpha);
      renderer.autoClear = currentAutoClear;
    }
  }

  override setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width * this._resolutionScale));
    const h = Math.max(1, Math.floor(height * this._resolutionScale));

    this.renderTargetID.setSize(w, h);
    this.renderTargetEdge.setSize(w, h);
    this.renderTargetBlur.setSize(w, h);
    this.edgeDetectionMaterial.setSize(w, h);
    this.blurMaterial.setSize(w, h);
  }

  override dispose(): void {
    super.dispose();
    this.renderTargetID.dispose();
    this.renderTargetEdge.dispose();
    this.renderTargetBlur.dispose();
    this.idMaterial.dispose();
    this.idMaterialInstanced.dispose();
    this.blurMaterial.dispose();
    this.edgeDetectionMaterial.dispose();
    this.fullscreenQuad.geometry.dispose();
    this.selection.clear();
    this.objectMaterialMap.clear();
  }

  // Selection management methods
  public setSelection(actors: Actor[]): this {
    this.selection.set(actors);
    return this;
  }

  public addSelectedActors(actors: Actor[]): this {
    for (const actor of actors) this.selection.add(actor);
    return this;
  }

  public addSelectedActor(actor: Actor): this {
    this.selection.add(actor);
    return this;
  }

  public removeSelectedActors(actors: Actor[]): this {
    for (const actor of actors) this.selection.delete(actor);
    return this;
  }

  public removeSelectedActor(actor: Actor): this {
    this.selection.delete(actor);
    return this;
  }

  public clearSelection(): void {
    this.selection.clear();
  }

  /** Sets the hidden objects set (objects that should not render outlines) */
  public setHiddenObjects(hiddenObjects: ReadonlySet<THREE.Object3D>): void {
    this.hiddenObjects = hiddenObjects;
  }

  /** Sets the excluded objects set (objects excluded from post-processing, including outlines) */
  public setExcludedObjects(excludedObjects: ReadonlySet<THREE.Object3D>): void {
    this.excludedObjects = excludedObjects;
  }

  /** Sets the layer used to identify excluded objects */
  public setExcludedLayer(layer: number): void {
    this.excludedLayer = layer;
    this.excludedLayerMask.set(layer);
  }
}

/** Manages Actor selection and tracks meshes for each actor */
class ActorSelection extends Set<Actor> {
  private actorMeshMap = new Map<Actor, THREE.Mesh[]>();

  override add(actor: Actor): this {
    if (actor && !this.has(actor)) {
      super.add(actor);
      this.collectMeshesForActor(actor);
    }
    return this;
  }

  override delete(actor: Actor): boolean {
    if (this.has(actor)) {
      this.actorMeshMap.delete(actor);
      return super.delete(actor);
    }
    return false;
  }

  override clear(): void {
    super.clear();
    this.actorMeshMap.clear();
  }

  set(actors: Actor[]): this {
    this.clear();
    for (const actor of actors) this.add(actor);
    return this;
  }

  getMeshesForActor(actor: Actor): THREE.Mesh[] {
    return this.actorMeshMap.get(actor) ?? [];
  }

  /** Re-collects meshes for an actor and returns them. Handles dynamically loaded content like GLTFs. */
  refreshMeshesForActor(actor: Actor): THREE.Mesh[] {
    this.collectMeshesForActor(actor);
    return this.actorMeshMap.get(actor) ?? [];
  }

  /** Removes null/undefined actors from the selection */
  pruneInvalidActors(): void {
    for (const actor of this) {
      if (!actor) {
        this.actorMeshMap.delete(actor);
        super.delete(actor);
      }
    }
  }

  private collectMeshesForActor(actor: Actor): void {
    const meshes: THREE.Mesh[] = [];
    const sceneObjects = actor.getComponents(SceneComponent);
    for (const sceneObject of sceneObjects) {
      sceneObject.traverse((child: THREE.Object3D) => {
        if (isMesh(child)) {
          meshes.push(child);
        }
      });
    }
    this.actorMeshMap.set(actor, meshes);
  }
}

/** Material for rendering objects with unique ID colors */
class ObjectIDMaterial extends THREE.ShaderMaterial {
  constructor() {
    super({
      name: 'ObjectIDMaterial',
      vertexShader: /* glsl */ `
        #include <skinning_pars_vertex>
        void main() {
          #include <skinbase_vertex>
          #include <begin_vertex>
          #include <skinning_vertex>

          vec4 localPosition = vec4(transformed, 1.0);

          #ifdef USE_INSTANCING
            localPosition = instanceMatrix * localPosition;
          #endif

          vec4 worldPosition = modelMatrix * localPosition;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 objectID;
        void main() {
          gl_FragColor = vec4(objectID, 1.0);
        }
      `,
      uniforms: {
        objectID: { value: new THREE.Vector3(0, 0, 0) }
      },
      depthTest: true,
      depthWrite: true // Keep depth write enabled for proper occlusion
    });
  }

  setObjectID(id: THREE.Vector3): void {
    this.uniforms.objectID.value.copy(id);
  }
}

/** Material for detecting edges based on object ID discontinuities */
class EdgeDetectionMaterial extends THREE.ShaderMaterial {
  constructor(idTexture: THREE.Texture, edgeWidth: number = 2) {
    super({
      name: 'EdgeDetectionMaterial',
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = position.xy * 0.5 + 0.5;
          gl_Position = vec4(position.xy, 1.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D idTexture;
        uniform vec2 texelSize;
        uniform float edgeWidth;
        varying vec2 vUv;

        void main() {
          vec3 centerID = texture2D(idTexture, vUv).rgb;

          // If center pixel has no object (background), no edge
          if (length(centerID) < 0.001) {
            gl_FragColor = vec4(0.0);
            return;
          }

          float edge = 0.0;
          float threshold = 0.001;
          vec2 offset = texelSize * edgeWidth;

          // Sample in 8 directions
          vec3 top = texture2D(idTexture, vUv + vec2(0.0, offset.y)).rgb;
          vec3 bottom = texture2D(idTexture, vUv + vec2(0.0, -offset.y)).rgb;
          vec3 left = texture2D(idTexture, vUv + vec2(-offset.x, 0.0)).rgb;
          vec3 right = texture2D(idTexture, vUv + vec2(offset.x, 0.0)).rgb;
          vec3 topLeft = texture2D(idTexture, vUv + vec2(-offset.x, offset.y)).rgb;
          vec3 topRight = texture2D(idTexture, vUv + vec2(offset.x, offset.y)).rgb;
          vec3 bottomLeft = texture2D(idTexture, vUv + vec2(-offset.x, -offset.y)).rgb;
          vec3 bottomRight = texture2D(idTexture, vUv + vec2(offset.x, -offset.y)).rgb;

          // Edge exists if any neighbor has a different ID (including background with ID 0)
          if (distance(centerID, top) > threshold ||
              distance(centerID, bottom) > threshold ||
              distance(centerID, left) > threshold ||
              distance(centerID, right) > threshold ||
              distance(centerID, topLeft) > threshold ||
              distance(centerID, topRight) > threshold ||
              distance(centerID, bottomLeft) > threshold ||
              distance(centerID, bottomRight) > threshold) {
            edge = 1.0;
          }

          gl_FragColor = vec4(edge, edge, edge, 1.0);
        }
      `,
      uniforms: {
        idTexture: { value: idTexture },
        texelSize: {
          value: new THREE.Vector2(
            1.0 / (typeof window !== 'undefined' ? window.innerWidth : 1920),
            1.0 / (typeof window !== 'undefined' ? window.innerHeight : 1080)
          )
        },
        edgeWidth: { value: edgeWidth }
      },
      depthTest: false,
      depthWrite: false
    });
  }

  get edgeWidth(): number {
    return this.uniforms.edgeWidth.value;
  }

  set edgeWidth(value: number) {
    this.uniforms.edgeWidth.value = Math.max(1, value);
  }

  setSize(width: number, height: number): void {
    this.uniforms.texelSize.value.set(1.0 / width, 1.0 / height);
  }
}

/** Material for blurring the edge texture */
class BlurMaterial extends THREE.ShaderMaterial {
  constructor(edgeTexture: THREE.Texture) {
    super({
      name: 'BlurMaterial',
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = position.xy * 0.5 + 0.5;
          gl_Position = vec4(position.xy, 1.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D edgeTexture;
        uniform vec2 texelSize;
        uniform float blurAmount;
        varying vec2 vUv;

        void main() {
          float result = 0.0;
          float total = 0.0;

          // Gaussian-like blur kernel
          int radius = int(blurAmount);
          for (int x = -3; x <= 3; x++) {
            for (int y = -3; y <= 3; y++) {
              if (abs(x) > radius || abs(y) > radius) continue;
              vec2 offset = vec2(float(x), float(y)) * texelSize * blurAmount;
              float weight = 1.0 - length(vec2(float(x), float(y))) / (float(radius) + 1.0);
              result += texture2D(edgeTexture, vUv + offset).r * weight;
              total += weight;
            }
          }

          float blurred = total > 0.0 ? result / total : 0.0;
          gl_FragColor = vec4(blurred, blurred, blurred, 1.0);
        }
      `,
      uniforms: {
        edgeTexture: { value: edgeTexture },
        texelSize: {
          value: new THREE.Vector2(
            1.0 / (typeof window !== 'undefined' ? window.innerWidth : 1920),
            1.0 / (typeof window !== 'undefined' ? window.innerHeight : 1080)
          )
        },
        blurAmount: { value: 1.0 }
      },
      depthTest: false,
      depthWrite: false
    });
  }

  get blurAmount(): number {
    return this.uniforms.blurAmount.value;
  }

  set blurAmount(value: number) {
    this.uniforms.blurAmount.value = Math.max(0, value);
  }

  setSize(width: number, height: number): void {
    this.uniforms.texelSize.value.set(1.0 / width, 1.0 / height);
  }
}

// Fragment shader for blending edges with scene
const fragmentShader = /* glsl */ `
uniform sampler2D edgeTexture;
uniform vec3 visibleEdgeColor;
uniform float edgeStrength;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  float edge = texture2D(edgeTexture, uv).r * edgeStrength;
  // Blend between input color and edge color based on edge intensity
  vec3 color = mix(inputColor.rgb, visibleEdgeColor, edge);
  outputColor = vec4(color, max(edge, inputColor.a));
}
`;

/** Type guard to check if an object is a mesh (including InstancedMesh) */
function isMesh(object: THREE.Object3D): object is THREE.Mesh {
  return 'isMesh' in object && (object as THREE.Mesh).isMesh === true;
}

/** Type guard to check if an object is an instanced mesh */
function isInstancedMesh(object: THREE.Object3D): object is THREE.InstancedMesh {
  return 'isInstancedMesh' in object && (object as THREE.InstancedMesh).isInstancedMesh === true;
}

// ============================================================================
// TSL (WebGPU) Object Outline Effect
// ============================================================================

/** Cached ID material for a mesh in the outline selection */
interface TSLMeshCacheEntry {
  idMaterial: MeshBasicNodeMaterial;
  objectID: THREE.Vector3;
}

/**
 * TSL-based Object Outline Effect for WebGPU renderer.
 *
 * This implements an ID-buffer based outline effect that matches the visual appearance
 * of the WebGL ObjectOutlineEffect. Unlike TSL OutlineNode which uses depth/normal-based
 * edge detection, this uses per-object ID colors to detect edges between different objects.
 *
 * Algorithm:
 * 1. Render selected objects to an ID buffer with unique colors per object/actor
 * 2. Edge detection pass: detect discontinuities in the ID buffer
 * 3. Optional blur pass for softer edges
 * 4. Composite edges onto the scene
 */
export class TSLObjectOutlineEffect {
  private renderer: WebGPURenderer;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;

  // Configuration
  private _edgeStrength: number;
  private _edgeWidth: number;
  private _visibleEdgeColor: THREE.Color;
  private _hiddenEdgeColor: THREE.Color;
  private _resolutionScale: number;
  private _useActorGrouping: boolean;
  private _edgeBlur: number;
  private _edgeGlow: number;

  // TSL uniforms for compositing (allows reactive updates without rebuilding pipeline)
  private _edgeStrengthUniform = uniform(1.0);
  private _visibleEdgeColorUniform = uniform(new THREE.Color(0xffffff));
  private _hiddenEdgeColorUniform = uniform(new THREE.Color(0x888888));
  private _edgeGlowUniform = uniform(0.0);

  // TSL uniforms for edge detection and blur (allows parameter changes without material rebuild)
  private _edgeWidthUniform = uniform(1.5);
  private _blurAmountUniform = uniform(0.0);
  private _texelSizeUniform = uniform(new THREE.Vector2(1 / 1920, 1 / 1080));

  // Scene camera near/far in linear units, used by edge depth predicate.
  // Updated each frame from the active scene camera (NOT the QuadMesh ortho camera,
  // whose near/far would be 0/1 and break perspectiveDepthToViewZ).
  private _cameraNearUniform = uniform(0.1);
  private _cameraFarUniform = uniform(1000.0);

  // Render targets
  private renderTargetID: THREE.RenderTarget;
  private renderTargetSelectedID: THREE.RenderTarget;
  private renderTargetEdge: THREE.RenderTarget;
  private renderTargetBlurTemp: THREE.RenderTarget;
  private renderTargetBlur: THREE.RenderTarget;

  // Materials
  private edgeDetectionMaterial: NodeMaterial;
  private copyMaterial: NodeMaterial;
  private blurHMaterial: NodeMaterial;
  private blurVMaterial: NodeMaterial;

  // Cached depth-only material for WebGPU (reused across frames)
  private depthOnlyMaterial: MeshBasicNodeMaterial | null = null;
  private readonly sceneDepthRenderer = new SceneDepthRenderer();

  // Fullscreen quad for post-processing passes
  private quadMesh: QuadMesh;
  // Root scene for ID/depth passes; prevents GenesysScene.onBeforeRender from restoring environment.
  private readonly idPassScene = new THREE.Scene();

  // Selection management - tracks Actor -> Mesh[] mapping
  private _selectedActors: Set<Actor> = new Set();
  private actorMeshMap = new Map<Actor, THREE.Mesh[]>();

  // Hidden/excluded objects
  private hiddenObjects: Set<THREE.Object3D> = new Set();
  private excludedObjects: Set<THREE.Object3D> = new Set();

  // Layer used to identify excluded objects (default: 30)
  private excludedLayer: number = 30;
  private excludedLayerMask: THREE.Layers = new THREE.Layers();

  // Edge texture for compositing
  private _edgeTexture: THREE.Texture;
  private sharedSceneDepthTexture: THREE.Texture | null;
  private useSharedSceneDepth: boolean;

  // Track current render target size to avoid unnecessary setSize calls
  private currentWidth = 0;
  private currentHeight = 0;

  // Performance optimization: cache ID materials per mesh
  private selectionDirty = true;
  private meshCache = new Map<THREE.Mesh, TSLMeshCacheEntry>();

  // Throttle updateSelectionCache to avoid per-frame scene traversals
  private updateCacheFrameCounter = 0;
  private static readonly UPDATE_CACHE_INTERVAL = 30;
  private emptySelectionTargetsCleared = false;

  // Temporary storage for material/layers swapping during ID render (cleared each frame)
  private objectMaterialMap = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
  private objectLayersMap = new Map<THREE.Mesh, number>();

  // Layer used for batched ID pass rendering (must not conflict with excludedLayer=30)
  private static readonly ID_RENDER_LAYER = 31;
  private idRenderLayerMask = 1 << TSLObjectOutlineEffect.ID_RENDER_LAYER;

  // Cached Color for getClearColor to avoid per-frame allocation
  private _clearColorCache = new THREE.Color();

  constructor(renderer: WebGPURenderer, config: ObjectOutlineEffectInit = {}) {
    this.renderer = renderer;
    this.sharedSceneDepthTexture = config.sharedSceneDepthTexture ?? null;
    this.useSharedSceneDepth = this.sharedSceneDepthTexture !== null;
    this._edgeStrength = config.edgeStrength ?? OBJECT_OUTLINE_DEFAULTS.edgeStrength;
    this._edgeWidth = config.edgeThickness ?? OBJECT_OUTLINE_DEFAULTS.edgeThickness;
    this._visibleEdgeColor = new THREE.Color(config.visibleEdgeColor ?? OBJECT_OUTLINE_DEFAULTS.visibleEdgeColor);
    this._hiddenEdgeColor = new THREE.Color(config.hiddenEdgeColor ?? OBJECT_OUTLINE_DEFAULTS.hiddenEdgeColor);
    this._resolutionScale = config.resolutionScale ?? OBJECT_OUTLINE_DEFAULTS.resolutionScale;
    this._useActorGrouping = config.useActorGrouping ?? OBJECT_OUTLINE_DEFAULTS.useActorGrouping;
    this._edgeBlur = config.edgeBlur ?? OBJECT_OUTLINE_DEFAULTS.edgeBlur;
    this._edgeGlow = Math.max(0, config.edgeGlow ?? OBJECT_OUTLINE_DEFAULTS.edgeGlow);

    // Initialize uniforms with config values
    this._edgeStrengthUniform.value = this._edgeStrength;
    this._visibleEdgeColorUniform.value.copy(this._visibleEdgeColor);
    this._hiddenEdgeColorUniform.value.copy(this._hiddenEdgeColor);
    this._edgeWidthUniform.value = this._edgeWidth;
    this._blurAmountUniform.value = this._edgeBlur;
    this._edgeGlowUniform.value = this._edgeGlow;

    // Initialize exclusion layer mask
    this.excludedLayerMask.set(this.excludedLayer);

    const _screenW = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const _screenH = typeof window !== 'undefined' ? window.innerHeight : 1080;
    const width = Math.max(1, Math.floor(_screenW * this._resolutionScale));
    const height = Math.max(1, Math.floor(_screenH * this._resolutionScale));

    // Initialise texel size uniform
    this._texelSizeUniform.value.set(1.0 / width, 1.0 / height);

    // Object ID buffer - each object rendered with unique colour
    this.renderTargetID = new THREE.RenderTarget(width, height, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.renderTargetID.texture.name = 'TSLObjectOutline.ID';
    // Attach a sampleable depth texture so the edge pass can distinguish occlusion seams
    // between selected objects (front-most gets an edge) from coplanar/adjacent seams.
    // FloatType is required so sampling with `texture(depthTex).sample(uv).r` returns
    // a clean linearizable value in WebGPU (UnsignedIntType depth is opaque/quantized).
    this.renderTargetID.depthTexture = new THREE.DepthTexture(width, height);
    this.renderTargetID.depthTexture.type = THREE.FloatType;
    this.renderTargetID.depthTexture.name = 'TSLObjectOutline.IDDepth';

    // Selected-only ID buffer (without occluder priming) used for hidden-edge extraction.
    this.renderTargetSelectedID = new THREE.RenderTarget(width, height, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.renderTargetSelectedID.texture.name = 'TSLObjectOutline.SelectedID';
    this.renderTargetSelectedID.depthTexture = new THREE.DepthTexture(width, height);
    this.renderTargetSelectedID.depthTexture.type = THREE.FloatType;
    this.renderTargetSelectedID.depthTexture.name = 'TSLObjectOutline.SelectedIDDepth';

    // Edge detection result (2-channel): r=visible edge, g=hidden edge.
    this.renderTargetEdge = new THREE.RenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGFormat,
      type: THREE.HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.renderTargetEdge.texture.name = 'TSLObjectOutline.Edge';

    // Intermediate blur target for separable H-pass (2-channel)
    this.renderTargetBlurTemp = new THREE.RenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGFormat,
      type: THREE.HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.renderTargetBlurTemp.texture.name = 'TSLObjectOutline.BlurTemp';

    // Final blur result (2-channel)
    this.renderTargetBlur = new THREE.RenderTarget(width, height, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGFormat,
      type: THREE.HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.renderTargetBlur.texture.name = 'TSLObjectOutline.Blur';

    // Use Three's WebGPU fullscreen quad so render-target UV flipping matches TSL postprocess passes.
    this.quadMesh = new QuadMesh(new THREE.MeshBasicMaterial());

    // Create post-processing materials using TSL
    this.edgeDetectionMaterial = this.createEdgeDetectionMaterial(width, height);
    this.copyMaterial = this.createCopyMaterial();
    this.blurHMaterial = this.createBlurHMaterial();
    this.blurVMaterial = this.createBlurVMaterial();

    // Initialize edge texture reference (always use blur target to avoid texture feedback in WebGPU)
    this._edgeTexture = this.renderTargetBlur.texture;

    // just a wrapper, no neede to update the matrix
    this.idPassScene.matrixWorldAutoUpdate = false;
  }

  /** Get the edge texture for compositing */
  get edgeTexture(): THREE.Texture { return this._edgeTexture; }

  /** Get the ID render target (for depth masking) - exposes the private renderTargetID */
  get idRenderTarget(): THREE.RenderTarget { return this.renderTargetID; }

  /** Get edge strength */
  get edgeStrength(): number { return this._edgeStrength; }
  set edgeStrength(value: number) {
    this._edgeStrength = value;
    this._edgeStrengthUniform.value = value;
  }

  /** Get edge strength uniform for TSL compositing */
  get edgeStrengthUniform() { return this._edgeStrengthUniform; }

  /** Get edge width */
  get edgeWidth(): number { return this._edgeWidth; }
  set edgeWidth(value: number) {
    this._edgeWidth = value;
    // Update uniform instead of recreating material - reactive update without performance hit
    this._edgeWidthUniform.value = value;
  }

  /** Get visible edge color */
  get visibleEdgeColor(): number { return this._visibleEdgeColor.getHex(); }
  set visibleEdgeColor(value: number) {
    this._visibleEdgeColor.setHex(value);
    this._visibleEdgeColorUniform.value.copy(this._visibleEdgeColor);
  }

  /** Get visible edge color as THREE.Color */
  get visibleEdgeColorValue(): THREE.Color { return this._visibleEdgeColor; }

  /** Get visible edge color uniform for TSL compositing */
  get visibleEdgeColorUniform() { return this._visibleEdgeColorUniform; }

  /** Get hidden edge color */
  get hiddenEdgeColor(): number { return this._hiddenEdgeColor.getHex(); }
  set hiddenEdgeColor(value: number) {
    this._hiddenEdgeColor.setHex(value);
    this._hiddenEdgeColorUniform.value.copy(this._hiddenEdgeColor);
  }

  /** Get hidden edge color uniform for TSL compositing */
  get hiddenEdgeColorUniform() { return this._hiddenEdgeColorUniform; }

  /** Get resolution scale */
  get resolutionScale(): number { return this._resolutionScale; }
  set resolutionScale(value: number) {
    const oldScale = this._resolutionScale;
    this._resolutionScale = value;
    const currentWidth = this.renderTargetID.width;
    const currentHeight = this.renderTargetID.height;
    const originalWidth = oldScale > 0 ? currentWidth / oldScale : currentWidth;
    const originalHeight = oldScale > 0 ? currentHeight / oldScale : currentHeight;
    const newWidth = Math.max(1, Math.floor(originalWidth * value));
    const newHeight = Math.max(1, Math.floor(originalHeight * value));
    this.setSize(newWidth / value, newHeight / value);
  }

  /** Get whether actor grouping is enabled */
  get useActorGrouping(): boolean { return this._useActorGrouping; }
  set useActorGrouping(value: boolean) { this._useActorGrouping = value; }

  /** Get edge blur amount */
  get edgeBlur(): number { return this._edgeBlur; }
  set edgeBlur(value: number) {
    this._edgeBlur = Math.max(0, value);
    // Update uniform instead of recreating material - reactive update without performance hit
    this._blurAmountUniform.value = this._edgeBlur;
  }

  /** Get edge glow amount */
  get edgeGlow(): number { return this._edgeGlow; }
  set edgeGlow(value: number) {
    this._edgeGlow = Math.max(0, value);
    this._edgeGlowUniform.value = this._edgeGlow;
  }

  /** Get edge glow uniform for TSL compositing */
  get edgeGlowUniform() { return this._edgeGlowUniform; }

  /** Get selected actors */
  get selection(): ReadonlySet<Actor> { return this._selectedActors; }

  public hasSelection(): boolean { return this._selectedActors.size > 0; }

  /** Create the edge detection material using NodeMaterial (uses uniforms for reactive updates) */
  private createEdgeDetectionMaterial(_width: number, _height: number): NodeMaterial {
    const material = new NodeMaterial();
    material.name = 'TSLEdgeDetectionMaterial';

    const sceneDepthTex = texture(this.sharedSceneDepthTexture ?? this.renderTargetID.depthTexture!);
    const selectedIdTex = texture(this.renderTargetSelectedID.texture);
    const selectedDepthTex = texture(this.renderTargetSelectedID.depthTexture!);
    // Use uniforms instead of static values for reactive updates without material rebuild
    const texelSizeValue = this._texelSizeUniform;
    const edgeWidthValue = this._edgeWidthUniform;

    // Edge output channels:
    //   r = visible selected edge intensity
    //   g = hidden selected edge intensity when selected depth is occluded by scene depth
    material.fragmentNode = Fn(() => {
      const vUv = uv();
      const centerSelectedSample = selectedIdTex.sample(vUv);
      const centerSceneRawDepth = sceneDepthTex.sample(vUv).r;
      const centerSelectedRawDepth = selectedDepthTex.sample(vUv).r;
      // perspectiveDepthToViewZ returns negative view-Z; negate to get a positive
      // linear distance that grows with depth (handles reversedDepthBuffer internally).
      // We pass our own near/far uniforms because the active camera during this pass
      // is the QuadMesh OrthographicCamera (near=0, far=1), not the scene camera.
      const centerSceneViewDist = perspectiveDepthToViewZ(centerSceneRawDepth, this._cameraNearUniform, this._cameraFarUniform).negate();
      const centerSelectedViewDist = perspectiveDepthToViewZ(centerSelectedRawDepth, this._cameraNearUniform, this._cameraFarUniform).negate();

      const alphaThreshold = float(0.5);
      const idEpsilon = float(0.001);

      const centerSelectedRgbSum = centerSelectedSample.r.add(centerSelectedSample.g).add(centerSelectedSample.b);
      const centerSelectedSelected = centerSelectedSample.a.greaterThan(alphaThreshold).and(centerSelectedRgbSum.greaterThan(idEpsilon));
      // Keep epsilon small and capped so near-coplanar selected meshes (e.g. low-offset planes over ground)
      // still produce stable outlines without reintroducing heavy contact-seam artefacts at long distances.
      const occlusionEps = min(float(0.02), max(float(0.0005), centerSelectedViewDist.mul(0.0005)));
      const centerHidden = centerSelectedSelected.and(centerSelectedViewDist.greaterThan(centerSceneViewDist.add(occlusionEps)));

      const offset = texelSizeValue.mul(edgeWidthValue);

      // 8 neighbor UVs (cardinal + diagonal)
      const uvTop = vUv.add(vec2(float(0.0), offset.y));
      const uvBottom = vUv.add(vec2(float(0.0), offset.y.negate()));
      const uvLeft = vUv.add(vec2(offset.x.negate(), float(0.0)));
      const uvRight = vUv.add(vec2(offset.x, float(0.0)));
      const uvTopLeft = vUv.add(vec2(offset.x.negate(), offset.y));
      const uvTopRight = vUv.add(vec2(offset.x, offset.y));
      const uvBottomLeft = vUv.add(vec2(offset.x.negate(), offset.y.negate()));
      const uvBottomRight = vUv.add(vec2(offset.x, offset.y.negate()));

      const sampleSelectedEdge = (nUv: Node<'vec2'>, diagonalWeight: number) => {
        const nSelected = selectedIdTex.sample(nUv);
        const nSelectedRgbSum = nSelected.r.add(nSelected.g).add(nSelected.b);
        const nSelectedSelected = nSelected.a.greaterThan(alphaThreshold).and(nSelectedRgbSum.greaterThan(idEpsilon));

        const rDiff = max(centerSelectedSample.r.sub(nSelected.r), nSelected.r.sub(centerSelectedSample.r));
        const gDiff = max(centerSelectedSample.g.sub(nSelected.g), nSelected.g.sub(centerSelectedSample.g));
        const bDiff = max(centerSelectedSample.b.sub(nSelected.b), nSelected.b.sub(centerSelectedSample.b));
        const idDiff = max(max(rDiff, gDiff), bDiff);
        const idEdge = smoothstep(float(0.001), float(0.004), idDiff);

        const silhouetteEdge = select(nSelectedSelected.not(), float(1.0), float(0.0));
        const interEdge = select(nSelectedSelected, idEdge, float(0.0));

        return max(silhouetteEdge, interEdge).mul(float(diagonalWeight));
      };

      const d1 = sampleSelectedEdge(uvTop, 1.0);
      const d2 = sampleSelectedEdge(uvBottom, 1.0);
      const d3 = sampleSelectedEdge(uvLeft, 1.0);
      const d4 = sampleSelectedEdge(uvRight, 1.0);
      const d5 = sampleSelectedEdge(uvTopLeft, 0.707);
      const d6 = sampleSelectedEdge(uvTopRight, 0.707);
      const d7 = sampleSelectedEdge(uvBottomLeft, 0.707);
      const d8 = sampleSelectedEdge(uvBottomRight, 0.707);

      // Cardinal weight 1.0 + diagonal 0.707 → max accumulated value ≈ 6.83.
      const edgeCount = d1.add(d2).add(d3).add(d4).add(d5).add(d6).add(d7).add(d8);

      // 1 strong neighbor → light edge, 2+ → full intensity.
      const selectedEdge = select(centerSelectedSelected, smoothstep(float(0.0), float(2.0), edgeCount), float(0.0));
      const visibleEdge = select(centerHidden.not(), selectedEdge, float(0.0));
      const hiddenEdge = select(centerHidden, selectedEdge, float(0.0));

      return vec4(visibleEdge, hiddenEdge, float(0.0), float(1.0));
    })();

    material.depthTest = false;
    material.depthWrite = false;
    material.transparent = true;
    material.blending = THREE.AdditiveBlending;

    return material;
  }

  /** Create a trivial copy material (1 sample) for when blur is disabled */
  private createCopyMaterial(): NodeMaterial {
    const material = new NodeMaterial();
    material.name = 'TSLCopyMaterial';

    const edgeTex = texture(this.renderTargetEdge.texture);

    material.fragmentNode = Fn(() => {
      const edgeSample = edgeTex.sample(uv());
      return vec4(edgeSample.r, edgeSample.g, float(0.0), float(1.0));
    })();

    material.depthTest = false;
    material.depthWrite = false;

    return material;
  }

  /** Create the horizontal blur material (normalized separable Gaussian, 9 taps along X). */
  private createBlurHMaterial(): NodeMaterial {
    const material = new NodeMaterial();
    material.name = 'TSLBlurHMaterial';

    const edgeTex = texture(this.renderTargetEdge.texture);
    const texelSizeValue = this._texelSizeUniform;
    const blurAmountUniform = this._blurAmountUniform;

    material.fragmentNode = Fn(() => {
      const vUv = uv();
      const texelStep = vec2(float(1.0), float(0.0)).mul(texelSizeValue).mul(max(blurAmountUniform, float(0.001)));
      const weights = [0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216];

      let result: Node<'vec2'> = edgeTex.sample(vUv).rg.mul(float(weights[0]));

      for (let x = 1; x < weights.length; x++) {
        const offset = texelStep.mul(float(x));
        const sampleA = edgeTex.sample(vUv.add(offset)).rg;
        const sampleB = edgeTex.sample(vUv.sub(offset)).rg;
        result = result.add(sampleA.add(sampleB).mul(float(weights[x])));
      }

      return vec4(result.x, result.y, float(0.0), float(1.0));
    })();

    material.depthTest = false;
    material.depthWrite = false;

    return material;
  }

  /** Create the vertical blur material (normalized separable Gaussian, 9 taps along Y). */
  private createBlurVMaterial(): NodeMaterial {
    const material = new NodeMaterial();
    material.name = 'TSLBlurVMaterial';

    // V-pass reads from the intermediate H-pass result
    const blurTempTex = texture(this.renderTargetBlurTemp.texture);
    const texelSizeValue = this._texelSizeUniform;
    const blurAmountUniform = this._blurAmountUniform;

    material.fragmentNode = Fn(() => {
      const vUv = uv();
      const texelStep = vec2(float(0.0), float(1.0)).mul(texelSizeValue).mul(max(blurAmountUniform, float(0.001)));
      const weights = [0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216];

      let result: Node<'vec2'> = blurTempTex.sample(vUv).rg.mul(float(weights[0]));

      for (let y = 1; y < weights.length; y++) {
        const offset = texelStep.mul(float(y));
        const sampleA = blurTempTex.sample(vUv.add(offset)).rg;
        const sampleB = blurTempTex.sample(vUv.sub(offset)).rg;
        result = result.add(sampleA.add(sampleB).mul(float(weights[y])));
      }

      return vec4(result.x, result.y, float(0.0), float(1.0));
    })();

    material.depthTest = false;
    material.depthWrite = false;

    return material;
  }

  /**
   * Material used to prime the ID buffer with unselected occluders.
   * Writes a sentinel value of (rgb=0, alpha=1) so the edge shader can distinguish:
   *   - sky/empty:        alpha = 0
   *   - unselected occluder: alpha = 1, rgb sum ≈ 0
   *   - selected mesh:    alpha = 1, rgb sum > 0 (encoded ID)
   * Depth is written normally so the subsequent selected-ID pass occludes correctly.
   */
  private getDepthOnlyMaterial(): MeshBasicNodeMaterial {
    if (!this.depthOnlyMaterial) {
      this.depthOnlyMaterial = new MeshBasicNodeMaterial();
      this.depthOnlyMaterial.colorNode = vec3(0, 0, 0);
      this.depthOnlyMaterial.depthTest = true;
      this.depthOnlyMaterial.depthWrite = true;
      this.depthOnlyMaterial.transparent = false;
      this.depthOnlyMaterial.fog = false;
      this.depthOnlyMaterial.toneMapped = false;
    }

    return this.depthOnlyMaterial;
  }

  /** Encode object ID to RGB color (supports up to 16.7M unique objects) */
  private encodeObjectID(id: number): THREE.Vector3 {
    const r = (id & 0xFF) / 255;
    const g = ((id >> 8) & 0xFF) / 255;
    const b = ((id >> 16) & 0xFF) / 255;
    return new THREE.Vector3(r, g, b);
  }

  private renderSceneForIdPass(profileName: string, overrideMaterial: THREE.Material | null = null): void {
    if (!this.scene || !this.camera) return;

    THREE.ProfilerService.begin(profileName);

    const savedOverrideMaterial = this.idPassScene.overrideMaterial;
    const originalParent = this.scene.parent;
    try {
      this.idPassScene.overrideMaterial = overrideMaterial;
      this.idPassScene.add(this.scene);
      this.renderer.render(this.idPassScene, this.camera);
    } finally {
      this.idPassScene.remove(this.scene);
      this.idPassScene.overrideMaterial = savedOverrideMaterial;
      if (originalParent) {
        originalParent.add(this.scene);
      }
    }

    THREE.ProfilerService.end(profileName);
  }

  /** Render all visible scene geometry into renderTargetID depth, independent of selection state. */
  @THREE.profile
  private renderSceneDepthPass(sceneDepthLayerMask: number): void {
    if (!this.scene || !this.camera) return;

    this.sceneDepthRenderer.render({
      scene: this.scene,
      camera: this.camera,
      sceneDepthLayerMask,
      depthMaterial: this.getDepthOnlyMaterial(),
      renderScene: (profileName, overrideMaterial) => this.renderSceneForIdPass(profileName, overrideMaterial),
      depthProfileName: 'depth pass',
      spriteProfileName: 'sprite pass',
    });
  }

  /** Collect meshes for an actor */
  private collectMeshesForActor(actor: Actor): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    const sceneComponents = actor.getComponents(SceneComponent);
    for (const component of sceneComponents) {
      component.traverse((child: THREE.Object3D) => {
        if (isMesh(child)) {
          meshes.push(child);
        }
      });
    }
    this.actorMeshMap.set(actor, meshes);
    return meshes;
  }

  /** Rebuild the selection cache when selection changes (full rebuild) */
  private rebuildSelectionCache(): void {
    // Dispose old cached materials
    for (const entry of this.meshCache.values()) {
      entry.idMaterial.dispose();
    }
    this.meshCache.clear();
    this.actorMeshMap.clear();

    this.updateSelectionCache();
    this.selectionDirty = false;
  }

  /** Update selection cache incrementally - adds new meshes without full rebuild */
  private updateSelectionCache(): void {
    let idCounter = this.meshCache.size + 1;

    for (const actor of this._selectedActors) {
      // Re-collect meshes to handle dynamically loaded content (e.g., GLTF loading)
      const meshes = this.collectMeshesForActor(actor);
      const actorID = this._useActorGrouping ? this.encodeObjectID(idCounter++) : null;

      for (const mesh of meshes) {
        if (!mesh.material) continue;
        if (this.meshCache.has(mesh)) continue;

        // Create unique ID material for this mesh (each mesh needs its own baked ID color)
        // Use MeshBasicNodeMaterial which has proper vertex processing for instancing/skinning
        const objectID = actorID ?? this.encodeObjectID(idCounter++);
        const idMat = new MeshBasicNodeMaterial();
        // Set the color directly - MeshBasicNodeMaterial handles vertex transforms including instancing
        idMat.colorNode = vec3(objectID.x, objectID.y, objectID.z);
        idMat.depthTest = true;
        idMat.depthWrite = true; // Keep depth write enabled for proper occlusion
        // ID pass must be invariant; fog/tone mapping would corrupt flat object IDs.
        idMat.fog = false;
        idMat.toneMapped = false;

        // Cache the ID material and its color
        this.meshCache.set(mesh, {
          idMaterial: idMat,
          objectID,
        });
      }
    }
  }

  /** Render the outline effect passes */
  @THREE.profile
  public render(): void {
    if (!this.scene || !this.camera) return;

    // Prune invalid actors
    for (const actor of this._selectedActors) {
      if (!actor) {
        this.actorMeshMap.delete(actor);
        this._selectedActors.delete(actor);
        this.selectionDirty = true;
      }
    }

    if (this._selectedActors.size === 0) {
      // Clear edge textures once when selection transitions to empty.
      if (!this.emptySelectionTargetsCleared) {
        const currentRenderTarget = this.renderer.getRenderTarget();
        this.renderer.setRenderTarget(this.renderTargetSelectedID);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
        this.renderer.clearDepth();
        this.renderer.setRenderTarget(this.renderTargetEdge);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
        this.renderer.setRenderTarget(this.renderTargetBlurTemp);
        this.renderer.clearColor();
        this.renderer.setRenderTarget(this.renderTargetBlur);
        this.renderer.clearColor();
        this.renderer.setRenderTarget(currentRenderTarget);
        this.emptySelectionTargetsCleared = true;
      }
      return;
    }
    this.emptySelectionTargetsCleared = false;

    // Rebuild cache if selection changed, otherwise check for new meshes (handles async loading)
    if (this.selectionDirty) {
      this.rebuildSelectionCache();
    } else {
      // Incrementally add any newly loaded meshes (throttled to avoid per-frame traversals)
      this.updateCacheFrameCounter++;
      if (this.updateCacheFrameCounter >= TSLObjectOutlineEffect.UPDATE_CACHE_INTERVAL) {
        this.updateCacheFrameCounter = 0;
        this.updateSelectionCache();
      }
    }

    // Sync near/far uniforms from the scene camera so the edge shader can linearize depth.
    // Falls back to defaults for non-perspective cameras (the predicate degrades gracefully).
    const cam = this.camera as THREE.PerspectiveCamera & { near?: number; far?: number };
    if (typeof cam.near === 'number') this._cameraNearUniform.value = cam.near;
    if (typeof cam.far === 'number') this._cameraFarUniform.value = cam.far;

    // Save renderer state
    const currentRenderTarget = this.renderer.getRenderTarget();
    const currentAutoClear = this.renderer.autoClear;
    const currentClearColor = this._clearColorCache;
    (this.renderer as unknown as { getClearColor(color: THREE.Color): void }).getClearColor(currentClearColor);
    const currentClearAlpha = this.renderer.getClearAlpha();

    try {
      this.renderer.autoClear = false;

      // Step 1: Render selected objects and excluded objects to ID buffer
      this.renderer.setRenderTarget(this.renderTargetID);
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.clearColor();
      this.renderer.clearDepth();
      this.objectMaterialMap.clear();

      // Step 1: Render selected meshes with unique ID colors using batched layer-based rendering
      // Swap materials and assign ID render layer to all valid meshes, then render scene once
      this.objectMaterialMap.clear();
      this.objectLayersMap.clear();

      for (const [mesh, { idMaterial }] of this.meshCache) {
        // Check visibility and exclusion each frame (these can change dynamically)
        let isVisible = mesh.visible;
        let isHidden = false;
        let isExcluded = false;
        let obj: THREE.Object3D | null = mesh;
        while (obj) {
          if (!obj.visible) isVisible = false;
          if (this.hiddenObjects.has(obj)) isHidden = true;
          // Check if object is on the exclusion layer (more efficient than set lookup)
          if (obj.layers.test(this.excludedLayerMask)) isExcluded = true;
          obj = obj.parent;
        }
        // Skip hidden/excluded meshes
        if (!isVisible || !mesh.material || isHidden || isExcluded) continue;

        // Skip if already processed (same mesh in multiple actors)
        if (this.objectMaterialMap.has(mesh)) continue;

        // Store original material and layers
        this.objectMaterialMap.set(mesh, mesh.material);
        this.objectLayersMap.set(mesh, mesh.layers.mask);

        // Swap to ID material and assign to ID render layer
        mesh.material = idMaterial;
        mesh.layers.mask = this.idRenderLayerMask;
      }

      // Batch render: reuse shared scene depth when available, then render all selected IDs once.
      // The selected target stores the frontmost selected actor ID per pixel.
      if (this.objectMaterialMap.size > 0) {
        const savedCameraLayers = this.camera.layers.mask;
        try {
          if (!this.useSharedSceneDepth) {
            // Scene depth is rendered independently of selection so selecting the
            // occluder does not change visible/hidden classification.
            this.renderer.setRenderTarget(this.renderTargetID);
            this.renderer.setClearColor(0x000000, 0);
            this.renderer.clearColor();
            this.renderer.clearDepth();
            this.renderSceneDepthPass(savedCameraLayers | this.idRenderLayerMask);
          }

          this.renderer.setRenderTarget(this.renderTargetEdge);
          this.renderer.setClearColor(0x000000, 0);
          this.renderer.clearColor();

          this.camera.layers.mask = this.idRenderLayerMask;

          this.renderer.setRenderTarget(this.renderTargetSelectedID);
          this.renderer.setClearColor(0x000000, 0);
          this.renderer.clearColor();
          this.renderer.clearDepth();
          this.renderSceneForIdPass('ID pass');

          this.renderer.setRenderTarget(this.renderTargetEdge);
          this.quadMesh.material = this.edgeDetectionMaterial;
          this.quadMesh.render(this.renderer);
        } finally {
          this.camera.layers.mask = savedCameraLayers;
        }
      }

      // Restore original materials and layers
      for (const [mesh, material] of this.objectMaterialMap) {
        mesh.material = material;
      }
      for (const [mesh, layersMask] of this.objectLayersMap) {
        mesh.layers.mask = layersMask;
      }

      // Step 2: Edge detection has already written the selected outlines.
      if (this.objectMaterialMap.size === 0) {
        this.renderer.setRenderTarget(this.renderTargetEdge);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
      }

      // Step 3: Copy or blur pass (always writes to renderTargetBlur to avoid texture feedback)
      if (this._edgeBlur > 0 && this.objectMaterialMap.size > 0) {
        // Separable Gaussian blur: H-pass edge→temp, V-pass temp→blur (14 samples vs 49)
        this.renderer.setRenderTarget(this.renderTargetBlurTemp);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
        this.quadMesh.material = this.blurHMaterial;
        this.quadMesh.render(this.renderer);

        this.renderer.setRenderTarget(this.renderTargetBlur);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
        this.quadMesh.material = this.blurVMaterial;
        this.quadMesh.render(this.renderer);
      } else {
        // No blur: trivial copy (1 sample) to avoid texture feedback
        this.renderer.setRenderTarget(this.renderTargetBlur);
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.clearColor();
        if (this.objectMaterialMap.size > 0) {
          this.quadMesh.material = this.copyMaterial;
          this.quadMesh.render(this.renderer);
        }
      }
    } finally {
      this.renderer.setRenderTarget(currentRenderTarget);
      this.renderer.setClearColor(currentClearColor.getHex(), currentClearAlpha);
      this.renderer.autoClear = currentAutoClear;
    }
  }

  /** Set the scene and camera */
  public setSceneAndCamera(scene: THREE.Scene, camera: THREE.Camera): void {
    // Mark selection dirty if scene/camera changed (handles renderer switching and initial setup)
    const sceneChanged = this.scene !== scene;
    const cameraChanged = this.camera !== camera;
    if (sceneChanged || cameraChanged) {
      this.selectionDirty = true;
    }
    this.scene = scene;
    this.camera = camera;
  }

  /** Set the size of render targets (skips if size unchanged) */
  public setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width * this._resolutionScale));
    const h = Math.max(1, Math.floor(height * this._resolutionScale));

    // Skip if size hasn't changed
    if (w === this.currentWidth && h === this.currentHeight) return;
    this.currentWidth = w;
    this.currentHeight = h;

    this.renderTargetID.setSize(w, h);
    this.renderTargetSelectedID.setSize(w, h);
    this.renderTargetEdge.setSize(w, h);
    this.renderTargetBlurTemp.setSize(w, h);
    this.renderTargetBlur.setSize(w, h);

    // Update texelSize uniform instead of recreating materials - much faster than material rebuild
    this._texelSizeUniform.value.set(1.0 / w, 1.0 / h);
  }

  // Selection management methods
  public setSelection(actors: Actor[]): this {
    this._selectedActors.clear();
    this.actorMeshMap.clear();
    for (const actor of actors) {
      if (actor) this._selectedActors.add(actor);
    }
    this.selectionDirty = true;
    this.emptySelectionTargetsCleared = false;
    return this;
  }

  public addSelectedActors(actors: Actor[]): this {
    for (const actor of actors) {
      if (actor) this._selectedActors.add(actor);
    }
    this.selectionDirty = true;
    this.emptySelectionTargetsCleared = false;
    return this;
  }

  public addSelectedActor(actor: Actor): this {
    if (actor) this._selectedActors.add(actor);
    this.selectionDirty = true;
    this.emptySelectionTargetsCleared = false;
    return this;
  }

  public removeSelectedActors(actors: Actor[]): this {
    for (const actor of actors) {
      this._selectedActors.delete(actor);
      this.actorMeshMap.delete(actor);
    }
    this.selectionDirty = true;
    return this;
  }

  public removeSelectedActor(actor: Actor): this {
    this._selectedActors.delete(actor);
    this.actorMeshMap.delete(actor);
    this.selectionDirty = true;
    return this;
  }

  public clearSelection(): void {
    this._selectedActors.clear();
    this.actorMeshMap.clear();
    this.selectionDirty = true;
  }

  /** Sets the hidden objects set */
  public setHiddenObjects(hiddenObjects: ReadonlySet<THREE.Object3D>): void {
    this.hiddenObjects = hiddenObjects as Set<THREE.Object3D>;
  }

  /** Sets the excluded objects set */
  public setExcludedObjects(excludedObjects: ReadonlySet<THREE.Object3D>): void {
    this.excludedObjects = excludedObjects as Set<THREE.Object3D>;
  }

  /** Sets the layer used to identify excluded objects */
  public setExcludedLayer(layer: number): void {
    this.excludedLayer = layer;
    this.excludedLayerMask.set(layer);
  }

  /** Dispose of resources */
  public dispose(): void {
    this.renderTargetID.dispose();
    this.renderTargetSelectedID.dispose();
    this.renderTargetEdge.dispose();
    this.renderTargetBlurTemp.dispose();
    this.renderTargetBlur.dispose();
    this.edgeDetectionMaterial.dispose();
    this.copyMaterial.dispose();
    this.blurHMaterial.dispose();
    this.blurVMaterial.dispose();
    this.depthOnlyMaterial?.dispose();
    this.sceneDepthRenderer.dispose();
    // Dispose cached ID materials
    for (const entry of this.meshCache.values()) {
      entry.idMaterial.dispose();
    }
    this.meshCache.clear();
    this._selectedActors.clear();
    this.actorMeshMap.clear();
    this.objectMaterialMap.clear();
    this.objectLayersMap.clear();
  }
}

/**
 * Unified Object Outline effect for both WebGL and WebGPU.
 *
 * Uses ID-buffer based rendering to create outlines around selected objects.
 */
export class ObjectOutlineEffect extends IPostProcessEffect<ObjectOutlineEffectConfig> {
  override readonly type = PostProcessPass.ObjectOutline;
  override readonly order = PostProcessRenderOrder.ObjectOutline;

  private webglEffect: WebGLObjectOutlineEffect | null = null;
  private webgpuEffect: TSLObjectOutlineEffect | null = null;

  // Cache selection for when effect is created after selection is set
  private pendingSelection: Set<Actor> = new Set();

  // Cached Vector2 for getSize calls to avoid per-frame allocation
  private _sizeVector = new THREE.Vector2();

  private _showHiddenEdgeUniform = uniform(1.0);

  override getDefaultConfig(): ObjectOutlineEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.ObjectOutline] };
  }

  override createWebGLEffect(config: ObjectOutlineEffectConfig, context: EffectContext): WebGLEffectData {
    const defaults = this.getDefaultConfig();

    this.webglEffect?.dispose();
    this.webglEffect = new WebGLObjectOutlineEffect(context.scene, context.camera, {
      // NOTE: BlendFunction is an enum; numeric 0 is SKIP (disables blending/effect)
      blendFunction: BlendFunction.NORMAL,
      edgeStrength: config.edgeStrength ?? defaults.edgeStrength,
      edgeThickness: config.edgeThickness ?? defaults.edgeThickness,
      visibleEdgeColor: config.visibleEdgeColor ?? defaults.visibleEdgeColor,
      resolutionScale: config.resolutionScale ?? defaults.resolutionScale,
      useActorGrouping: config.useActorGrouping ?? defaults.useActorGrouping,
      edgeBlur: config.edgeBlur ?? defaults.edgeBlur,
      showHiddenEdge: config.showHiddenEdge ?? defaults.showHiddenEdge,
    });

    if (this.pendingSelection.size > 0) {
      this.webglEffect.addSelectedActors(Array.from(this.pendingSelection));
    }

    return { effects: [this.webglEffect] };
  }

  override applyWebGLConfig(effectData: WebGLEffectData, config: ObjectOutlineEffectConfig): void {
    const defaults = this.getDefaultConfig();
    const outlineEffect = effectData.effects[0] as WebGLObjectOutlineEffect;

    if (outlineEffect) {
      outlineEffect.edgeStrength = config.edgeStrength ?? defaults.edgeStrength!;
      outlineEffect.edgeWidth = config.edgeThickness ?? defaults.edgeThickness!;
      outlineEffect.visibleEdgeColor = config.visibleEdgeColor ?? defaults.visibleEdgeColor!;
      outlineEffect.resolutionScale = config.resolutionScale ?? defaults.resolutionScale!;
      outlineEffect.useActorGrouping = config.useActorGrouping ?? defaults.useActorGrouping!;
      outlineEffect.edgeBlur = config.edgeBlur ?? defaults.edgeBlur!;
    }
  }

  override createWebGPUNode(config: ObjectOutlineEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();

    this._showHiddenEdgeUniform.value = (config.showHiddenEdge ?? defaults.showHiddenEdge ?? true) ? 1.0 : 0.0;

    this.webgpuEffect = new TSLObjectOutlineEffect(context.renderer as WebGPURenderer, {
      edgeStrength: config.edgeStrength ?? defaults.edgeStrength,
      edgeThickness: config.edgeThickness ?? defaults.edgeThickness,
      visibleEdgeColor: config.visibleEdgeColor ?? defaults.visibleEdgeColor,
      hiddenEdgeColor: config.hiddenEdgeColor ?? defaults.hiddenEdgeColor,
      resolutionScale: config.resolutionScale ?? defaults.resolutionScale,
      useActorGrouping: config.useActorGrouping ?? defaults.useActorGrouping,
      edgeBlur: config.edgeBlur ?? defaults.edgeBlur,
      edgeGlow: config.edgeGlow ?? defaults.edgeGlow,
      showHiddenEdge: config.showHiddenEdge ?? defaults.showHiddenEdge,
      sharedSceneDepthTexture: context.sharedSceneDepthTexture,
    });

    if (this.pendingSelection.size > 0) {
      this.webgpuEffect.addSelectedActors(Array.from(this.pendingSelection));
    }

    return {
      node: null as any,
      needsMRT: false,
      compose: (outputNode: any, _ctx: WebGPUComposeContext) => {
        if (!this.webgpuEffect) return outputNode;

        const edgeTex = texture(this.webgpuEffect.edgeTexture, screenUV);
        const edgeStrength = this.webgpuEffect.edgeStrengthUniform;
        const edgeGlow = this.webgpuEffect.edgeGlowUniform;

        const visibleEdgeIntensity = edgeTex.r.mul(edgeStrength);
        const hiddenEdgeIntensity = edgeTex.g.mul(edgeStrength).mul(this._showHiddenEdgeUniform);
        const visibleGlowIntensity = min(float(1.0), visibleEdgeIntensity.mul(edgeGlow));
        const hiddenGlowIntensity = min(float(1.0), hiddenEdgeIntensity.mul(edgeGlow));

        const withVisibleEdges = mix(
          outputNode,
          this.webgpuEffect.visibleEdgeColorUniform,
          min(float(1.0), visibleEdgeIntensity.add(visibleGlowIntensity)),
        );

        return mix(
          withVisibleEdges,
          this.webgpuEffect.hiddenEdgeColorUniform,
          min(float(1.0), hiddenEdgeIntensity.add(hiddenGlowIntensity)),
        );
      },
    };
  }

  override preRender(context: EffectContext & { hiddenObjects: ReadonlySet<THREE.Object3D>; excludedObjects: ReadonlySet<THREE.Object3D> }): void {
    if (this.webgpuEffect) {
      this.webgpuEffect.setSceneAndCamera(context.scene, context.camera);
      this.webgpuEffect.setHiddenObjects(context.hiddenObjects as Set<THREE.Object3D>);
      this.webgpuEffect.setExcludedObjects(context.excludedObjects as Set<THREE.Object3D>);
      const size = (context.renderer as WebGPURenderer).getSize(this._sizeVector);
      this.webgpuEffect.setSize(size.x, size.y);
      this.webgpuEffect.render();
    }
  }

  override applyWebGPUConfig(_node: WebGPUEffectNode, config: ObjectOutlineEffectConfig): void {
    const defaults = this.getDefaultConfig();

    this._showHiddenEdgeUniform.value = (config.showHiddenEdge ?? defaults.showHiddenEdge ?? true) ? 1.0 : 0.0;

    if (this.webgpuEffect) {
      if (config.edgeStrength !== undefined) this.webgpuEffect.edgeStrength = config.edgeStrength;
      if (config.edgeThickness !== undefined) this.webgpuEffect.edgeWidth = config.edgeThickness;
      if (config.visibleEdgeColor !== undefined) this.webgpuEffect.visibleEdgeColor = config.visibleEdgeColor;
      if (config.hiddenEdgeColor !== undefined) this.webgpuEffect.hiddenEdgeColor = config.hiddenEdgeColor;
      if (config.edgeBlur !== undefined) this.webgpuEffect.edgeBlur = config.edgeBlur;
      if (config.edgeGlow !== undefined) this.webgpuEffect.edgeGlow = config.edgeGlow;
    }
  }

  override requiresRebuild(oldConfig: ObjectOutlineEffectConfig, newConfig: ObjectOutlineEffectConfig): boolean {
    return (
      oldConfig.enabled !== newConfig.enabled ||
      oldConfig.resolutionScale !== newConfig.resolutionScale ||
      oldConfig.useActorGrouping !== newConfig.useActorGrouping
    );
  }

  override dispose(): void {
    this.webglEffect?.dispose();
    this.webgpuEffect?.dispose();
    this.webglEffect = null;
    this.webgpuEffect = null;
  }

  public addSelectedActors(...actors: Actor[]): void {
    for (const actor of actors) {
      this.pendingSelection.add(actor);
    }

    if (this.webglEffect) {
      this.webglEffect.addSelectedActors(actors);
    }
    if (this.webgpuEffect) {
      this.webgpuEffect.addSelectedActors(actors);
    }
  }

  public addSelectedActor(actor: Actor): void {
    this.pendingSelection.add(actor);

    if (this.webglEffect) {
      this.webglEffect.addSelectedActor(actor);
    }
    if (this.webgpuEffect) {
      this.webgpuEffect.addSelectedActor(actor);
    }
  }

  public removeSelectedActors(...actors: Actor[]): void {
    for (const actor of actors) {
      this.pendingSelection.delete(actor);
    }

    if (this.webglEffect) {
      this.webglEffect.removeSelectedActors(actors);
    }
    if (this.webgpuEffect) {
      this.webgpuEffect.removeSelectedActors(actors);
    }
  }

  public removeSelectedActor(actor: Actor): void {
    this.pendingSelection.delete(actor);

    if (this.webglEffect) {
      this.webglEffect.removeSelectedActor(actor);
    }
    if (this.webgpuEffect) {
      this.webgpuEffect.removeSelectedActor(actor);
    }
  }

  public clearSelection(): void {
    this.pendingSelection.clear();
    this.webglEffect?.clearSelection();
    this.webgpuEffect?.clearSelection();
  }

  public setSelection(actors: Actor[]): void {
    this.pendingSelection.clear();
    for (const actor of actors) {
      this.pendingSelection.add(actor);
    }

    if (this.webglEffect) {
      this.webglEffect.setSelection(actors);
    }
    if (this.webgpuEffect) {
      this.webgpuEffect.setSelection(actors);
    }
  }

  public get selection(): ReadonlySet<Actor> {
    if (this.webglEffect) {
      return this.webglEffect.selection;
    }
    if (this.webgpuEffect) {
      return this.webgpuEffect.selection;
    }
    return this.pendingSelection;
  }

  public hasWebGPUSelection(): boolean {
    return this.webgpuEffect?.hasSelection() ?? this.pendingSelection.size > 0;
  }

  public getWebGLEffect(): WebGLObjectOutlineEffect | null {
    return this.webglEffect;
  }

  public getWebGPUEffect(): TSLObjectOutlineEffect | null {
    return this.webgpuEffect;
  }
}
